import {
  TrendingUp,
  Wallet,
  HandCoins,
  Banknote,
  CircleDollarSign,
  Receipt,
  Coins,
  Building2,
  Briefcase,
  FileText,
  ClipboardCheck,
} from "lucide-react";

export default function IconPreview() {
  const iconOptions = [
    {
      category: "Current",
      icons: [
        { name: "TrendingUp (Current)", Icon: TrendingUp, description: "Upward trending chart" },
      ],
    },
    {
      category: "Collection-Focused Icons",
      icons: [
        { name: "Wallet", Icon: Wallet, description: "Money/payment collection" },
        { name: "HandCoins", Icon: HandCoins, description: "Money exchange/collection" },
        { name: "Banknote", Icon: Banknote, description: "Direct money representation" },
        { name: "CircleDollarSign", Icon: CircleDollarSign, description: "Currency-focused" },
        { name: "Receipt", Icon: Receipt, description: "Invoice/payment focused" },
        { name: "Coins", Icon: Coins, description: "Simple money representation" },
      ],
    },
    {
      category: "Professional/Business Icons",
      icons: [
        { name: "Building2", Icon: Building2, description: "Corporate/business focus" },
        { name: "Briefcase", Icon: Briefcase, description: "Professional business tool" },
        { name: "FileText", Icon: FileText, description: "Document/invoice management" },
        { name: "ClipboardCheck", Icon: ClipboardCheck, description: "Task completion/tracking" },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Icon Preview for Access Collect</h1>
          <p className="text-muted-foreground">
            Compare different icon options for the application title
          </p>
        </div>

        {iconOptions.map((category) => (
          <div key={category.category} className="mb-12">
            <h2 className="text-2xl font-semibold mb-6">{category.category}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.icons.map(({ name, Icon, description }) => (
                <div
                  key={name}
                  className="border rounded-lg p-6 hover:shadow-lg transition-shadow"
                >
                  {/* Preview as it would appear in sidebar */}
                  <div className="mb-4 p-4 bg-muted rounded-md">
                    <div className="flex items-center gap-2">
                      <Icon className="h-6 w-6" />
                      <h3 className="text-xl font-bold">Access Collect</h3>
                    </div>
                  </div>

                  {/* Icon details */}
                  <div className="space-y-2">
                    <p className="font-semibold text-sm">{name}</p>
                    <p className="text-sm text-muted-foreground">{description}</p>
                  </div>

                  {/* Larger icon preview */}
                  <div className="mt-4 flex justify-center p-4 bg-background rounded-md border">
                    <Icon className="h-12 w-12" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Custom Logo Options */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Custom Logo/Monogram Options</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* AC Monogram - Simple */}
            <div className="border rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="mb-4 p-4 bg-muted rounded-md">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 flex items-center justify-center font-bold text-sm bg-primary text-primary-foreground rounded">
                    AC
                  </div>
                  <h3 className="text-xl font-bold">Access Collect</h3>
                </div>
              </div>
              <div className="space-y-2">
                <p className="font-semibold text-sm">AC Monogram (Square)</p>
                <p className="text-sm text-muted-foreground">Simple branded monogram</p>
              </div>
              <div className="mt-4 flex justify-center p-4 bg-background rounded-md border">
                <div className="h-12 w-12 flex items-center justify-center font-bold text-2xl bg-primary text-primary-foreground rounded">
                  AC
                </div>
              </div>
            </div>

            {/* AC Monogram - Circle */}
            <div className="border rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="mb-4 p-4 bg-muted rounded-md">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 flex items-center justify-center font-bold text-sm bg-primary text-primary-foreground rounded-full">
                    AC
                  </div>
                  <h3 className="text-xl font-bold">Access Collect</h3>
                </div>
              </div>
              <div className="space-y-2">
                <p className="font-semibold text-sm">AC Monogram (Circle)</p>
                <p className="text-sm text-muted-foreground">Rounded branded monogram</p>
              </div>
              <div className="mt-4 flex justify-center p-4 bg-background rounded-md border">
                <div className="h-12 w-12 flex items-center justify-center font-bold text-2xl bg-primary text-primary-foreground rounded-full">
                  AC
                </div>
              </div>
            </div>

            {/* Icon with Background Circle */}
            <div className="border rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="mb-4 p-4 bg-muted rounded-md">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 flex items-center justify-center bg-primary text-primary-foreground rounded-full">
                    <HandCoins className="h-4 w-4" />
                  </div>
                  <h3 className="text-xl font-bold">Access Collect</h3>
                </div>
              </div>
              <div className="space-y-2">
                <p className="font-semibold text-sm">Icon with Background</p>
                <p className="text-sm text-muted-foreground">HandCoins with colored circle</p>
              </div>
              <div className="mt-4 flex justify-center p-4 bg-background rounded-md border">
                <div className="h-12 w-12 flex items-center justify-center bg-primary text-primary-foreground rounded-full">
                  <HandCoins className="h-8 w-8" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Color Variations */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Color Variations (Example with HandCoins)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="border rounded-lg p-6">
              <div className="mb-4 p-4 bg-muted rounded-md">
                <div className="flex items-center gap-2">
                  <HandCoins className="h-6 w-6 text-primary" />
                  <h3 className="text-xl font-bold">Access Collect</h3>
                </div>
              </div>
              <p className="text-sm font-semibold">Primary Color</p>
            </div>

            <div className="border rounded-lg p-6">
              <div className="mb-4 p-4 bg-muted rounded-md">
                <div className="flex items-center gap-2">
                  <HandCoins className="h-6 w-6 text-green-600" />
                  <h3 className="text-xl font-bold">Access Collect</h3>
                </div>
              </div>
              <p className="text-sm font-semibold">Green (Money)</p>
            </div>

            <div className="border rounded-lg p-6">
              <div className="mb-4 p-4 bg-muted rounded-md">
                <div className="flex items-center gap-2">
                  <HandCoins className="h-6 w-6 text-blue-600" />
                  <h3 className="text-xl font-bold">Access Collect</h3>
                </div>
              </div>
              <p className="text-sm font-semibold">Blue (Trust)</p>
            </div>

            <div className="border rounded-lg p-6">
              <div className="mb-4 p-4 bg-muted rounded-md">
                <div className="flex items-center gap-2">
                  <HandCoins className="h-6 w-6 text-amber-600" />
                  <h3 className="text-xl font-bold">Access Collect</h3>
                </div>
              </div>
              <p className="text-sm font-semibold">Amber (Gold)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
